import { ChangeDetectionStrategy, Component, input, output, signal, computed, inject } from '@angular/core';
import {
  CalendarView,
  CalendarDatePipe,
  CalendarPreviousViewDirective,
  CalendarNextViewDirective,
  CalendarTodayDirective,
} from 'angular-calendar';
import { CalendarIconComponent, CheckIconComponent, ChevronRightIconComponent } from '@kuiper/icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { DatePickerCalendarComponent } from '../../../date-picker/date-picker-calendar/date-picker-calendar.component';
import { CalendarHelperService } from '../../services/calendar-helper.service';
import { WorkingDaysResponse } from '../../../../../core/common/models/calendar.model';
import { DatePickerHelperService } from '../../../date-picker/services/date-picker-helper.service';

@Component({
  selector: 'lib-calendar-toolbar',
  imports: [
    CalendarPreviousViewDirective,
    CalendarNextViewDirective,
    CalendarTodayDirective,
    CalendarIconComponent,
    ChevronRightIconComponent,
    CalendarDatePipe,
    TranslocoPipe,
    CheckIconComponent,
    DatePickerCalendarComponent,
  ],
  templateUrl: './calendar-toolbar.component.html',
  styleUrl: './calendar-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CalendarToolbarComponent {
  readonly CalendarView = CalendarView;

  readonly calendarHelper = inject(CalendarHelperService);
  readonly datePickerHelper = inject(DatePickerHelperService);

  viewDate = input.required<Date>();
  view = input.required<CalendarView>();
  workingDays = input.required<WorkingDaysResponse>();
  weekStartsOnNumber = input<number>();

  viewChange = output<CalendarView>();
  viewDateChange = output<Date>();
  closeOpenMonthViewDay = output<void>();

  viewDropdownOpen = signal(false);
  toggleDatePickerCalendar = signal(false);
  calendarViews = signal<{ view: CalendarView, label: string }[]>([
    { view: CalendarView.Month, label: 'calendar_toolbar_month_view_item_label' },
    { view: CalendarView.Week, label: 'calendar_toolbar_week_view_item_label' },
    { view: CalendarView.Day, label: 'calendar_toolbar_day_view_item_label' },
  ]);
  selectedDateView = signal<Date>(new Date());

  currentMonth = signal<number>(new Date().getMonth());
  isShowMonths = signal(false);
  isShowYears = signal(false);

  firstDayOfWeek = computed<number>(() => this.calendarHelper.getWeekStartIndex(this.workingDays()?.start_day.value || 'sunday')); // specific day (e.g. 1 for Monday)
  weekStartsOnDate = computed<Date | undefined>(() => {
    if (this.view() === CalendarView.Week) {
      const currentDate = this.viewDate();
      const dayOfWeek = currentDate.getDay();
      const diff = (dayOfWeek < this.firstDayOfWeek()) ? (7 - this.firstDayOfWeek() + dayOfWeek) : (dayOfWeek - this.firstDayOfWeek());
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - diff);
      return startOfWeek;
    }
    return undefined;
  })

  setView(view: CalendarView): void {
    this.viewChange.emit(view);
    this.viewDropdownOpen.set(false);
  }

  onViewDateChange(date: Date): void {
    this.viewDateChange.emit(date);
    this.selectedDateView.set(date);
    this.closeOpenMonthViewDay.emit();
  }

  toggleViewDropdown() {
    this.viewDropdownOpen.set(!this.viewDropdownOpen());
  }

  toggleMonthPicker() {
    this.isShowMonths.set(!this.isShowMonths());
    this.isShowYears.set(false);
    this.toggleDatePickerCalendar.set(false);
  }

  handleMonthSelection(month: { name: string; value: number; }) {
    const today = new Date();
    const newDate = new Date(today.getFullYear(), month.value, 1);
    this.viewDateChange.emit(newDate);
    if (this.view() !== CalendarView.Month) {
      this.isShowMonths.set(false);
    }
    this.isShowYears.set(false);
  }

  toggleYearPicker(yearsContainer: HTMLDivElement) {
    this.isShowYears.set(!this.isShowYears());
    this.isShowMonths.set(false);
    this.toggleDatePickerCalendar.set(false);

    // Scroll to selected year after view updates
    const today = new Date();
    this.datePickerHelper.showYears(today.getFullYear(), yearsContainer);
  }

  handleYearSelection(year: number) {
    const today = new Date();
    const newDate = new Date(year, today.getMonth(), 1);
    this.viewDateChange.emit(newDate);

    this.isShowYears.set(false);
    this.isShowMonths.set(false);
  }
}